import RegionalAthletic from "./pages/RegionalAthletic";
import RegionalFootball from "./pages/RegionalFootball";
import RegionalNutrition from "./pages/RegionalNutrition";
import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { CartProvider } from "@/contexts/CartContext";
import { UserProfileProvider } from "@/hooks/useUserProfile";
import { SupportChat } from "@/components/SupportChat";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminRoute } from "@/components/AdminRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RouteNoindex } from "@/components/RouteNoindex";
import { currentLocalePrefix } from "@/lib/locale";
// Index stays eager — it's the LCP route and must render immediately.
import Index from "./pages/Index";
import { CookieConsent } from "./components/CookieConsent";
import { usePurchaseNotifications } from "@/hooks/usePurchaseNotifications";
import { useAnalytics } from "@/hooks/useAnalytics";

// Route-based code splitting — every non-Index page becomes its own chunk.
const ViewPlans = lazy(() => import("./pages/ViewPlans"));
const EliteAthletes = lazy(() => import("./pages/EliteAthletes"));
const NormalPeople = lazy(() => import("./pages/NormalPeople"));
const Reviews = lazy(() => import("./pages/Reviews"));
const ReviewRedirect = lazy(() => import("./pages/ReviewRedirect"));
const Login = lazy(() => import("./pages/Login"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Imprint = lazy(() => import("./pages/Imprint"));
const Terms = lazy(() => import("./pages/Terms"));
const Cookies = lazy(() => import("./pages/Cookies"));
const PerformanceDiagnostic = lazy(() => import("./pages/services/PerformanceDiagnostic"));
const GymTraining = lazy(() => import("./pages/services/GymTraining"));
const FootballTraining = lazy(() => import("./pages/services/FootballTraining"));
const Nutrition = lazy(() => import("./pages/services/Nutrition"));
const Psychology = lazy(() => import("./pages/services/Psychology"));
const LactateTest = lazy(() => import("./pages/services/LactateTest"));
const FatAnalysis = lazy(() => import("./pages/services/FatAnalysis"));
const FullBodyWeight = lazy(() => import("./pages/services/FullBodyWeight"));
const VO2Max = lazy(() => import("./pages/services/VO2Max"));
const Evaluation = lazy(() => import("./pages/services/Evaluation"));
const VO2MaxGuide = lazy(() => import("./pages/guides/VO2MaxGuide"));
const PersonalTrainerMunich = lazy(() => import("./pages/PersonalTrainerMunich"));
const NormalPeopleCategory = lazy(() => import("./pages/categories/NormalPeopleCategory"));
const EliteAthletesCategory = lazy(() => import("./pages/categories/EliteAthletesCategory"));
const FootballTrainingCategory = lazy(() => import("./pages/categories/FootballTrainingCategory"));
const AthleticTrainingCategory = lazy(() => import("./pages/categories/AthleticTrainingCategory"));
const WhatAreWeDoing = lazy(() => import("./pages/WhatAreWeDoing"));
const Checkout = lazy(() => import("./pages/Checkout"));
const MemberDashboard = lazy(() => import("./pages/MemberDashboard"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentFailed = lazy(() => import("./pages/PaymentFailed"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ProfileSettings = lazy(() => import("./pages/ProfileSettings"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminPayments = lazy(() => import("./pages/AdminPayments"));
const AdminCatalogHealth = lazy(() => import("./pages/AdminCatalogHealth"));
const AdminEmailLog = lazy(() => import("./pages/AdminEmailLog"));
const AdminOrdersLog = lazy(() => import("./pages/AdminOrdersLog"));
const AdminSelfTest = lazy(() => import("./pages/AdminSelfTest"));
const AdminPriceAudit = lazy(() => import("./pages/AdminPriceAudit"));
const AdminEmailHealth = lazy(() => import("./pages/AdminEmailHealth"));
const AdminSecurityEvents = lazy(() => import("./pages/AdminSecurityEvents"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const ManageNewsletter = lazy(() => import("./pages/ManageNewsletter"));
const AdminNewsletterLog = lazy(() => import("./pages/AdminNewsletterLog"));
const LocalLanding = lazy(() => import("./pages/LocalLanding"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const SiteMapPage = lazy(() => import("./pages/SiteMapPage"));
const ToolsHub = lazy(() => import("./pages/tools/ToolsHub"));
const ToolPage = lazy(() => import("./pages/tools/ToolPage"));
const BenchmarksHub = lazy(() => import("./pages/benchmarks/BenchmarksHub"));
const BenchmarkPage = lazy(() => import("./pages/benchmarks/BenchmarkPage"));

const queryClient = new QueryClient();

const ScrollToTopOnRouteChange = () => {
  const { pathname } = useLocation();
  useAnalytics();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const PurchaseNotificationBridge = () => {
  usePurchaseNotifications();
  return null;
};

// Locale prefix (/en, /de, /pl, /nl) becomes the router basename so every
// in-app link keeps the active locale and deep links stay valid.
const localePrefix = currentLocalePrefix();
const localeBasename = localePrefix ? `/${localePrefix}` : "/";

const App = () => (
  <QueryClientProvider client={queryClient}>
    <UserProfileProvider>
      <CartProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <SupportChat />
          <PurchaseNotificationBridge />
          <BrowserRouter basename={localeBasename}>
            <ScrollToTopOnRouteChange />
            <RouteNoindex />
            <ErrorBoundary>
              <Suspense fallback={null}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/view-plans" element={<ViewPlans />} />
                  <Route path="/elite-athletes" element={<EliteAthletes />} />
                  <Route path="/normal-people" element={<NormalPeople />} />
                  <Route path="/reviews" element={<Reviews />} />
                  <Route path="/case-study" element={<Navigate to="/reviews" replace />} />
                  <Route path="/review" element={<ReviewRedirect />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/imprint" element={<Imprint />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/cookies" element={<Cookies />} />
                  <Route path="/services/performance-diagnostic" element={<PerformanceDiagnostic />} />
                  <Route path="/services/gym-training" element={<GymTraining />} />
                  <Route path="/services/football-training" element={<FootballTraining />} />
                  <Route path="/services/nutrition" element={<Nutrition />} />
                  <Route path="/services/psychology" element={<Psychology />} />
                  <Route path="/services/lactate-test" element={<LactateTest />} />
                  <Route path="/services/fat-analysis" element={<FatAnalysis />} />
                  <Route path="/services/full-body-weight" element={<FullBodyWeight />} />
                  <Route path="/services/vo2-max" element={<VO2Max />} />
                  <Route path="/services/evaluation" element={<Evaluation />} />
                  <Route path="/guides/vo2-max" element={<VO2MaxGuide />} />
                  <Route path="/personal-trainer-munich" element={<PersonalTrainerMunich />} />
                  <Route
                    path="/personal-training-muenchen"
                    element={<Navigate to="/personal-trainer-munich" replace />}
                  />
                  <Route path="/categories/normal-people" element={<NormalPeopleCategory />} />
                  <Route path="/categories/elite-athletes" element={<EliteAthletesCategory />} />
                  <Route path="/categories/football-training" element={<FootballTrainingCategory />} />
                  <Route path="/categories/athletic-training" element={<AthleticTrainingCategory />} />
                  <Route path="/areas-of-expertise" element={<WhatAreWeDoing />} />
                  <Route path="/what-are-we-doing" element={<Navigate to="/areas-of-expertise" replace />} />
                  <Route
                    path="/services/personal-training"
                    element={<Navigate to="/services/gym-training" replace />}
                  />
                  <Route path="/services/sports-nutrition" element={<Navigate to="/services/nutrition" replace />} />
                  <Route
                    path="/services/athletic-performance"
                    element={<Navigate to="/categories/athletic-training" replace />}
                  />
                  <Route path="/services/lactate-vo2-testing" element={<Navigate to="/services/vo2-max" replace />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/payment-success" element={<PaymentSuccess />} />
                  <Route path="/payment-failed" element={<PaymentFailed />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <MemberDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <ProfileSettings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <AdminRoute>
                        <AdminDashboard />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/payments"
                    element={
                      <AdminRoute>
                        <AdminPayments />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/catalog-health"
                    element={
                      <AdminRoute>
                        <AdminCatalogHealth />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/email-log"
                    element={
                      <AdminRoute>
                        <AdminEmailLog />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/email-health"
                    element={
                      <AdminRoute>
                        <AdminEmailHealth />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/orders"
                    element={
                      <AdminRoute>
                        <AdminOrdersLog />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/self-test"
                    element={
                      <AdminRoute>
                        <AdminSelfTest />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/price-audit"
                    element={
                      <AdminRoute>
                        <AdminPriceAudit />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/security-events"
                    element={
                      <AdminRoute>
                        <AdminSecurityEvents />
                      </AdminRoute>
                    }
                  />
                  <Route path="/unsubscribe" element={<Unsubscribe />} />
                  <Route path="/manage-newsletter" element={<ManageNewsletter />} />
                  <Route
                    path="/admin/newsletter-log"
                    element={
                      <AdminRoute>
                        <AdminNewsletterLog />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/leistungsdiagnostik-trudering"
                    element={<LocalLanding slug="leistungsdiagnostik-trudering" />}
                  />
                  <Route path="/vo2max-test-riem" element={<LocalLanding slug="vo2max-test-riem" />} />
                  <Route
                    path="/personal-trainer-muenchen-ost"
                    element={<LocalLanding slug="personal-trainer-muenchen-ost" />}
                  />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/sitemap" element={<SiteMapPage />} />
                  <Route path="/tools" element={<ToolsHub />} />
                  <Route path="/tools/:slug" element={<ToolPage />} />
                  <Route path="/benchmarks" element={<BenchmarksHub />} />
                  <Route path="/benchmarks/:slug" element={<BenchmarkPage />} />

                  <Route path="/sports-nutrition-muenchen" element={<Navigate to="/services/nutrition" replace />} />
                  <Route
                    path="/athletic-training-muenchen"
                    element={<Navigate to="/categories/athletic-training" replace />}
                  />
                  <Route
                    path="/football-training-muenchen"
                    element={<Navigate to="/services/football-training" replace />}
                  />
                  <Route path="/athletic-training/:locationSlug" element={<RegionalAthletic />} />
                  <Route path="/football-training/:locationSlug" element={<RegionalFootball />} />
                  <Route path="/nutrition-coaching/:locationSlug" element={<RegionalNutrition />} />
                  {/* Common paths people/crawlers guess — redirect instead of 404 */}
                  <Route path="/about" element={<Navigate to="/areas-of-expertise" replace />} />
                  <Route path="/about-us" element={<Navigate to="/areas-of-expertise" replace />} />
                  <Route path="/services" element={<Navigate to="/areas-of-expertise" replace />} />
                  <Route path="/pricing" element={<Navigate to="/view-plans" replace />} />
                  <Route path="/prices" element={<Navigate to="/view-plans" replace />} />
                  <Route path="/plans" element={<Navigate to="/view-plans" replace />} />
                  <Route path="/contact" element={<Navigate to="/?scroll=contact" replace />} />


                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
            <CookieConsent />
          </BrowserRouter>
        </TooltipProvider>
      </CartProvider>
    </UserProfileProvider>
  </QueryClientProvider>
);

export default App;
